import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api from "../lib/api";
import { setSeo } from "../lib/seo";

const fmt = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export default function Blog() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setSeo({
      title: "Blog — AI insights, guides & strategy | vonmal",
      description:
        "Practical guides on building AI apps and agents fast: LLM integration, RAG, AI cost, and product strategy from the vonmal team.",
      url: "https://vonmal.com/blog",
    });
    api
      .get("/blog")
      .then((res) => setPosts(res.data || []))
      .catch(() => setPosts([]));
  }, []);

  const [featured, ...rest] = posts || [];

  return (
    <div className="bg-void min-h-screen">
      <Navbar />
      <main className="pt-28 pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-neon-cyan mb-3">// the vonmal blog</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
            AI, <span className="text-neon-cyan">decoded.</span>
          </h1>
          <p className="mt-4 text-lg text-white/60 max-w-2xl">
            Field notes on building cutting-edge AI apps and agents — fast, lean and production-ready. New articles published daily.
          </p>

          {posts === null && (
            <div data-testid="blog-loading" className="mt-16 font-mono text-sm text-neon-cyan/70 animate-pulse">
              // loading articles...
            </div>
          )}

          {posts !== null && posts.length === 0 && (
            <div data-testid="blog-empty" className="mt-16 font-mono text-sm text-white/50">
              // no articles yet — check back soon.
            </div>
          )}

          {/* Featured */}
          {featured && (
            <Link
              to={`/blog/${featured.slug}`}
              data-testid="blog-featured"
              className="group block mt-12 glow-border rounded-2xl border border-neon-cyan/15 bg-surface overflow-hidden md:grid md:grid-cols-2"
            >
              <div className="relative h-60 md:h-full overflow-hidden">
                <img src={featured.image} alt={featured.title} loading="lazy" className="w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-surface to-transparent" />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="flex items-center gap-3 text-xs font-mono text-white/45">
                  <span>{fmt(featured.date)}</span>
                  <span className="flex items-center gap-1"><Clock size={12} /> {featured.read_time}</span>
                </div>
                <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight group-hover:text-neon-cyan transition-colors">{featured.title}</h2>
                <p className="mt-3 text-white/60 leading-relaxed">{featured.excerpt}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-neon-cyan group-hover:gap-2 transition-all">
                  Read article <ArrowRight size={16} />
                </span>
              </div>
            </Link>
          )}

          {/* Grid */}
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {rest.map((post, i) => (
              <motion.div
                key={post.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 6) * 0.08 }}
              >
                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-card-${i}`}
                  className="group block glow-border h-full rounded-2xl border border-neon-cyan/15 bg-surface overflow-hidden"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img src={post.image} alt={post.title} loading="lazy" className="w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs font-mono text-white/45">
                      <span>{fmt(post.date)}</span>
                      <span className="flex items-center gap-1"><Clock size={12} /> {post.read_time}</span>
                    </div>
                    <h3 className="mt-2 text-lg font-bold tracking-tight group-hover:text-neon-cyan transition-colors">{post.title}</h3>
                    <p className="mt-2 text-sm text-white/55 leading-relaxed line-clamp-3">{post.excerpt}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
